import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import './Hero.css';
import logo from '../assets/logo.png';

const logoSrc = logo || '/logo192.png';

const TYPED_STRINGS = [
  'Web Development & Hosting',
  'AI Automation & Data Pipelines',
  'Cloud Infrastructure',
  'Cybersecurity & Compliance',
  'Managed IT & 24/7 Support',
];

const STATS = [
  { value: '50+',   label: 'Happy Clients' },
  { value: '5+',    label: 'Years Experience' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '24/7',  label: 'Support' },
];

const ORBS = [
  { w: 500, h: 500, top: '-10%', left: '-10%',  color: 'rgba(0,212,255,0.12)',   delay: '0s',  dur: '7s' },
  { w: 450, h: 450, top: '10%',  right: '-5%',  color: 'rgba(124,58,237,0.12)',  delay: '2s',  dur: '9s' },
  { w: 350, h: 350, bottom:'5%', left:  '20%',  color: 'rgba(16,185,129,0.10)',  delay: '4s',  dur: '8s' },
];

const Hero = () => {
  // Simple rotating headline (no typewriter delete animation)
  const [typedIndex, setTypedIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTypedIndex(i => (i + 1) % TYPED_STRINGS.length), 2600);
    return () => clearInterval(id);
  }, []);
  const displayText = TYPED_STRINGS[typedIndex];

  const fade   = { hidden: { opacity: 0, y: 24 }, visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: d, ease: 'easeOut' } }) };
  const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } } };

  return (
    <div className="hero-wrapper tech-grid-bg">
      {/* Particles background (WebGL) */}
      <Particles
        className="hero-particles"
        options={{
          fpsLimit: 60,
          interactivity: { detectsOn: 'canvas', events: { onHover: { enable: false, mode: 'repulse' }, resize: true } },
          particles: {
            number: { value: 60, density: { enable: true, area: 800 } },
            color: { value: ['#00d4ff', '#7c3aed', '#10b981'] },
            links: { color: '#2bb1ff', distance: 140, enable: true, opacity: 0.15, width: 1 },
            move: { enable: true, speed: 0.9, direction: 'none', outMode: 'out' },
            size: { value: { min: 1, max: 3 } },
            opacity: { value: 0.85, random: { enable: true, minimumValue: 0.3 } }
          },
          detectRetina: true
        }}
      />
      {/* ── Glowing background orbs ── */}
      {ORBS.map((o, i) => (
        <div key={i} className="hero-orb animate-blob" style={{
          width: o.w, height: o.h,
          top: o.top, left: o.left, right: o.right, bottom: o.bottom,
          background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
          animationDelay: o.delay, animationDuration: o.dur,
        }} />
      ))}

      {/* ── Scanline overlay ── */}
      <div className="hero-scanline" />

      {/* ── HERO HEADLINE ── */}
      <motion.section
        className="hero-main"
        variants={stagger} initial="hidden" animate="visible"
      >
        {/* Badge */}
        <motion.div variants={fade} custom={0.1} className="hero-badge">
          <span className="hero-badge-dot" />
          Next-Generation IT Solutions
        </motion.div>

        {/* Logo */}
        <motion.div variants={fade} custom={0.2} className="hero-logo-wrap">
          <motion.img
            src={logoSrc}
            alt="Radha IT"
            className="hero-logo"
            whileHover={{ scale: 1.05 }}
            onError={e => { e.target.src = '/logo192.png'; }}
          />
        </motion.div>

        {/* Tagline with typewriter */}
        <motion.div variants={fade} custom={0.3} className="hero-typewriter-row">
          <span className="hero-typewriter-label">We deliver </span>
          <span className="hero-typewriter-text">{displayText}</span>
          <span className="hero-cursor">|</span>
        </motion.div>

        {/* Description */}
        <motion.p variants={fade} custom={0.4} className="hero-desc">
          Empowering organisations with full-stack technology: web platforms, AI & automation,
          cloud infrastructure, cybersecurity, networking, AV systems and managed IT services.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div variants={fade} custom={0.5} className="hero-cta-row">
          <Link to="/contact" className="hero-btn-primary glow-btn-cyan">
            Get Free Consultation
            <motion.span aria-hidden="true">→</motion.span>
          </Link>
          <Link to="/services" className="hero-btn-secondary border-glow-cyan">
            Explore Services
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div variants={stagger} className="hero-stats">
          {STATS.map((s, i) => (
            <motion.div key={s.label} variants={fade} custom={0.6 + i * 0.1} className="hero-stat glass-card">
              <span className="hero-stat-value shimmer-text">{s.value}</span>
              <span className="hero-stat-label">{s.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* ── SERVICES PREVIEW ── */}
      <motion.section
        className="hero-services-section"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="section-chip">💼 Our Expertise</div>
          <h2 className="section-heading shimmer-text">Our Services</h2>
          <p className="section-sub">
            Comprehensive solutions designed to accelerate your business growth
          </p>
        </motion.div>

        <div className="hero-services-grid">
          {[
            { title: 'Web Development & Hosting', desc: 'Modern websites, custom WordPress plugins, e-commerce, and reliable cloud hosting.', link: '/services#web-development', icon: '🌐', color: '#00d4ff' },
            { title: 'IT Procurement',             desc: 'Source the best hardware and software with full vendor management and cost optimisation.',  link: '/services#it-procurement',   icon: '💻', color: '#7c3aed' },
            { title: 'Audio-Visual Solutions',     desc: 'End-to-end AV for conference rooms, digital signage, and interactive displays.',          link: '/services#av-solutions',      icon: '📺', color: '#10b981' },
            ].map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, rotateX: 3, rotateY: 6, scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="service-card glass-card"
            >
              <Link to={s.link} className="service-card-inner">
                <div className="service-card-icon" style={{ boxShadow: `0 0 20px ${s.color}40`, border: `1px solid ${s.color}40` }}>
                  <span>{s.icon}</span>
                </div>
                <h3 className="service-card-title" style={{ color: s.color }}>{s.title}</h3>
                <p className="service-card-desc">{s.desc}</p>
                <span className="service-card-link">
                  Learn More
                  <motion.span aria-hidden="true">→</motion.span>
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/services" className="hero-btn-secondary border-glow-cyan inline-flex">
            View All Services →
          </Link>
        </div>
      </motion.section>
    </div>
  );
};

export default Hero;
